import type { Rule } from '../../core/assetType.js';
import type { ValidationIssue } from '../../core/issues.js';
import type { Gameshow } from '../../schemas/gameshow.js';

import { toPointer } from '../../core/issues.js';

const EXPECTED_COUNT = 2;

export const scoreDefinitions: Rule<Gameshow> = {
  code: 'gameshow.score-definitions',
  check(input): ValidationIssue[] {
    const issues: ValidationIssue[] = [];
    const defs = input.scoreDefinitions;

    if (defs.length !== EXPECTED_COUNT) {
      const path: (string | number)[] = ['scoreDefinitions'];
      issues.push({
        code: 'gameshow.score-definitions.count',
        severity: 'error',
        message: `Expected exactly ${EXPECTED_COUNT} scoreDefinitions (player + contestant), found ${defs.length}.`,
        path,
        pointer: toPointer(path),
        currentValue: defs.length,
        constraint: { kind: 'range', detail: `exactly ${EXPECTED_COUNT}` },
      });
    }

    const characterIds = new Set(input.characters.map(c => c.id));

    defs.forEach((def, idx) => {
      if (def.initialValue !== 0) {
        const path: (string | number)[] = ['scoreDefinitions', idx, 'initialValue'];
        issues.push({
          code: 'gameshow.score-definitions.initial-value',
          severity: 'error',
          message: `scoreDefinition for '${def.characterId}' has initialValue ${def.initialValue} — must be 0.`,
          path,
          pointer: toPointer(path),
          currentValue: def.initialValue,
          constraint: { kind: 'enum', allowedValues: ['0'] },
        });
      }
      if (!characterIds.has(def.characterId)) {
        const path: (string | number)[] = ['scoreDefinitions', idx, 'characterId'];
        issues.push({
          code: 'gameshow.score-definitions.unknown-character',
          severity: 'error',
          message: `scoreDefinition.characterId '${def.characterId}' does not match any character id.`,
          path,
          pointer: toPointer(path),
          currentValue: def.characterId,
          constraint: { kind: 'reference', allowedValues: Array.from(characterIds) },
        });
      }
    });

    const player = input.characters.find(c => c.role === 'self');
    const contestant = input.characters.find(c => c.role === 'rival');
    if (player && !defs.some(d => d.characterId === player.id)) {
      const path: (string | number)[] = ['scoreDefinitions'];
      issues.push({
        code: 'gameshow.score-definitions.missing-player',
        severity: 'error',
        message: `No scoreDefinition for the player (characterId '${player.id}').`,
        path,
        pointer: toPointer(path),
        constraint: { kind: 'custom', detail: 'one scoreDefinition per character required' },
      });
    }
    if (contestant && !defs.some(d => d.characterId === contestant.id)) {
      const path: (string | number)[] = ['scoreDefinitions'];
      issues.push({
        code: 'gameshow.score-definitions.missing-contestant',
        severity: 'error',
        message: `No scoreDefinition for the contestant (characterId '${contestant.id}').`,
        path,
        pointer: toPointer(path),
        constraint: { kind: 'custom', detail: 'one scoreDefinition per character required' },
      });
    }

    return issues;
  },
};
